import { useEffect, useMemo, useState } from 'react'
import { faBookmark } from '@fortawesome/pro-duotone-svg-icons'

import Icon from '../icon/Icon'
import { useAuth } from '../../_hooks/use-auth'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import { useImportedListings } from '../../_hooks/use-imported-listings'
import { fetchDiscoverListingsByIds } from '../../_data/discover'
import { DiscoverListing } from '../../_types/quickpass-discover'
import { buildSavedListingPreviews } from './helpers'
import {
  SummaryRoot,
  SummaryHeader,
  SummaryTitle,
  SummaryLink,
  TileGrid,
  Tile,
  Thumb,
  ThumbImage,
  ThumbFallback,
  TileBody,
  TileTitle,
  TilePrice,
  TileMeta,
  SkeletonTile,
  EmptyTile,
  EmptyEmoji,
  EmptyTitle,
  EmptyBody,
} from './styled'

const DASHBOARD_PREVIEW_LIMIT = 4

/**
 * Dashboard widget previewing the renter's saved listings — up to four compact
 * tiles (imported listings first, then QuickCasa-network saves), each linking to
 * its detail page, with a "View all" link into /saved-listings. Shows skeletons
 * while network saves hydrate and a CTA empty state when there are none.
 *
 * @returns {JSX.Element | null} null while auth is loading
 */
const SavedListingsPreview = (): JSX.Element | null => {
  const { quickPassUser } = useAuth()
  const { profile } = useQuickPassProfile()
  const { listings: importedListings } = useImportedListings()
  const [networkListings, setNetworkListings] = useState<DiscoverListing[]>([])
  const [isHydratingNetwork, setIsHydratingNetwork] = useState<boolean>(false)

  const archivedIds = useMemo<Set<string>>(
    () => new Set(profile?.archivedListingIds ?? []),
    [profile],
  )
  const unarchivedImported = useMemo(
    () => importedListings.filter(entry => archivedIds.has(entry.id) === false),
    [importedListings, archivedIds],
  )
  const importedIdSet = useMemo<Set<string>>(
    () => new Set(importedListings.map(entry => entry.id)),
    [importedListings],
  )
  const networkOnlySavedIds = useMemo<string[]>(
    () =>
      (profile?.savedListingIds ?? []).filter(
        id => importedIdSet.has(id) === false,
      ),
    [profile, importedIdSet],
  )

  useEffect(() => {
    if (networkOnlySavedIds.length === 0) {
      setNetworkListings([])
      setIsHydratingNetwork(false)
      return
    }

    let isMounted = true
    setIsHydratingNetwork(true)

    void fetchDiscoverListingsByIds({
      ids: networkOnlySavedIds,
      quickPassUserId: quickPassUser?.id ?? null,
    }).then(hydrated => {
      if (isMounted === false) {
        return
      }
      setNetworkListings(hydrated)
      setIsHydratingNetwork(false)
    })

    return () => {
      isMounted = false
    }
  }, [networkOnlySavedIds, quickPassUser?.id])

  if (quickPassUser === null) {
    return null
  }

  const previews = buildSavedListingPreviews(
    unarchivedImported,
    networkListings,
    DASHBOARD_PREVIEW_LIMIT,
  )
  const hasPreviews = previews.length > 0
  const isLoadingPreviews = hasPreviews === false && isHydratingNetwork === true

  return (
    <SummaryRoot>
      <SummaryHeader>
        <SummaryTitle>Saved listings</SummaryTitle>
        {hasPreviews === true ? (
          <SummaryLink to="/saved-listings">View all</SummaryLink>
        ) : null}
      </SummaryHeader>

      {hasPreviews === true ? (
        <TileGrid>
          {previews.map(item => (
            <Tile key={item.id} to={item.to}>
              <Thumb>
                {item.imageUrl !== null ? (
                  <ThumbImage
                    src={item.imageUrl}
                    alt={item.title}
                    loading="lazy"
                  />
                ) : (
                  <ThumbFallback aria-hidden="true">
                    <Icon icon={faBookmark} label="Saved" />
                  </ThumbFallback>
                )}
              </Thumb>
              <TileBody>
                <TileTitle>{item.title}</TileTitle>
                <TilePrice>{item.priceLabel}</TilePrice>
                <TileMeta>{item.metaLabel}</TileMeta>
              </TileBody>
            </Tile>
          ))}
        </TileGrid>
      ) : null}

      {isLoadingPreviews === true ? (
        <TileGrid>
          <SkeletonTile />
          <SkeletonTile />
        </TileGrid>
      ) : null}

      {hasPreviews === false && isLoadingPreviews === false ? (
        <EmptyTile to="/saved-listings">
          <EmptyEmoji aria-hidden="true">
            <Icon icon={faBookmark} label="Bookmark" />
          </EmptyEmoji>
          <EmptyTitle>No saved listings yet</EmptyTitle>
          <EmptyBody>
            Save a place from any supported site — or import one by URL — and
            it&apos;ll show up right here.
          </EmptyBody>
        </EmptyTile>
      ) : null}
    </SummaryRoot>
  )
}

export default SavedListingsPreview
